import React, { useEffect, useMemo, useState } from "react";
import { useLazyLoadQuery, graphql } from "react-relay";
import StatTable from "../../../../../StatTable";
import ScrollShadow from "../../__shared/NFLTable";
import DraftkingsInfoOverlay from "../../../../../DraftKingsInfoOverlay";
import {
  PROJECTION_COLUMNS,
  filterProjectionRows,
  toProjectionRows,
} from "../projectionRows";

const GET_PROJECTIONS = graphql`
  query NFLProjectionsTableQuery($tzOffset: Int!) {
    nflGupProjections {
      name
      position
      team
      opp
      homeAway
      pointSpread
      gameTotal
      impliedTt
      projPoints
      projOwnership
      salary
      percentDkSalary
      dkDollarValue
      slatePlayer {
        id
      }
    }

    lastNflGupProjectionUpdate(tzOffset: $tzOffset)
  }
`;

const INITIAL_STATE = { sortBy: [{ id: "projPoints", desc: true }] };

export default function NFLProjectionsTable({
  positions,
  teams,
  fetchKey,
  onUpdatedAt,
  notPostedState,
  emptyState,
}) {
  const date = new Date();
  const tzOffset = date.getTimezoneOffset();
  const tzAbbrev = date
    .toLocaleTimeString("en-us", { timeZoneName: "short" })
    .split(" ")[2];

  const queryData = useLazyLoadQuery(
    GET_PROJECTIONS,
    { tzOffset },
    { fetchKey, fetchPolicy: fetchKey > 0 ? "network-only" : "store-or-network" }
  );

  const [activeDkPlayer, setActiveDkPlayer] = useState(undefined);

  if (!queryData.nflGupProjections) {
    throw new Error("nflGupProjections returned null");
  }

  const allRows = useMemo(
    () => toProjectionRows(queryData.nflGupProjections),
    [queryData]
  );

  const rows = useMemo(
    () => filterProjectionRows(allRows, { positions, teams }),
    [allRows, positions, teams]
  );

  const updatedAt =
    allRows.length > 0 && queryData.lastNflGupProjectionUpdate
      ? `${queryData.lastNflGupProjectionUpdate} ${tzAbbrev}`
      : null;

  useEffect(() => {
    onUpdatedAt(updatedAt);
  }, [onUpdatedAt, updatedAt]);

  if (allRows.length === 0) {
    return notPostedState;
  }

  if (rows.length === 0) {
    return emptyState;
  }

  return (
    <>
      <DraftkingsInfoOverlay
        show={activeDkPlayer !== undefined}
        playerId={activeDkPlayer}
        close={() => setActiveDkPlayer(undefined)}
      />
      <ScrollShadow>
        <StatTable
          columns={PROJECTION_COLUMNS}
          data={rows}
          paginate={true}
          pagination="compact"
          initialState={INITIAL_STATE}
          autoResetSortBy={false}
          showDKPlayerInfo={(playerId) => setActiveDkPlayer(playerId)}
        />
      </ScrollShadow>
    </>
  );
}
